// the control room: with ?controls=1 in the URL, a panel under the player for testing. Force a feature (run exactly
// as the clock runs it: intro, pages, outro, back to the loop), a :00/:30 bumper, a test interrupt (marked TEST on
// screen), or jump to any loop screen. A status line shows what's on air. It changes nothing on its own; the
// bumper reel comes up with it.
import {
	currentDisplay, showDisplay, getDisplay, isPlaying, message,
} from '../navigation.mjs';
import STATUS from '../status.mjs';
import { escapeHtml } from '../utils/string.mjs';
import { parseQueryString } from '../utils/setting.mjs';
import {
	runFeature, runBumper, busy, featureFor,
} from './scheduler.mjs';
import { isPlaying as bumperOn } from './bumper.mjs';
import forceInterrupt from './interrupt.mjs';
import { runShow } from './shows.mjs';
import { byHand } from './snapshot-display.mjs';

const { clock } = window.RHL;
const { voice } = clock;
const STATION = ['Russian Hill', 'Local'];

const FEATURES = [
	['market-closeup', 'Market Close-Up'],
	['news-desk', 'News Desk'],
	// the cameras (Oct 27, Phase C): the Live Look at :11/:41, the long block into the ID at :30/:00, and the night block
	// from :24/:54 (90 s holds; the hour picks the night pool and theme)
	['live-look', 'Live Look (1.5 min)', { look: true, seconds: 100 }],
	['views-block', 'Views, long (4 min)', { seconds: 232 }],
	['views-block', 'Views, night (6 min)', { seconds: 352, night: true }],
	// the show slot (Phase D): the show this hour would run
	['show', 'The show (:15/:45)'],
];
const BUMPERS = [
	['Station ID', { title: STATION, line: voice.stationId.lines[0], sting: 'bell-2' }],
	['Morning', { title: STATION, line: voice.dayparts.morning, sting: 'bell-2' }],
	['Weekend morning', { title: STATION, line: voice.dayparts.weekendMorning, sting: 'bell-2' }],
	['Evening', { title: STATION, line: voice.dayparts.evening, sting: 'bell-2' }],
	['Night sign-on', { title: ['Good', 'Night'], line: voice.dayparts.night, sting: 'foghorn' }],
	['Night sign-on, fog', { title: ['Good', 'Night'], line: voice.dayparts.nightFog, sting: 'foghorn' }],
	['Crab day', { title: STATION, line: '4C', sting: 'bell-2' }],
];
const INTERRUPTS = [['weather', 'Weather warning'], ['quake', 'Earthquake M4.6'], ['quake-major', 'Major: quake M5.4'], ['halt', 'Trading halt']];

// every display in nav-id order, features and the interrupt left out
const loopScreens = () => {
	const screens = [];
	for (let i = 0; i < 64; i += 1) {
		const display = getDisplay(i);
		if (display && !display.isFeature) screens.push(display);
	}
	return screens;
};

// a feature or interrupt only moves on while playing, so start play first
const ensurePlaying = () => {
	if (!isPlaying()) message({ type: 'navButton', message: 'play' });
};

// the clock's next feature from now, for the status line
const NAMES = {
	'market-closeup': 'Market Close-Up', 'news-desk': 'News Desk', 'views-block': 'Views', 'live-look': 'Live Look', show: 'the show',
};
const nextOnClock = () => {
	const now = new Date();
	for (let m = 1; m <= 60; m += 1) {
		const t = new Date(Math.floor(now.getTime() / 60000) * 60000 + m * 60000);
		const minute = Number(new Intl.DateTimeFormat('en-US', { timeZone: clock.timeZone, minute: 'numeric' }).format(t));
		const plan = featureFor(minute, t);
		if (plan) return `next on the clock: ${NAMES[plan.name] ?? plan.name} at ${t.toLocaleTimeString('en-US', { timeZone: clock.timeZone, hour: 'numeric', minute: '2-digit' })}`;
	}
	return null;
};

const status = () => {
	const current = currentDisplay();
	const parts = [
		isPlaying() ? 'Playing' : 'Paused',
		current ? `on ${current.name}` : 'no screen',
		bumperOn() ? 'bumper on air' : null,
		busy() && !bumperOn() ? 'feature or interrupt on air' : null,
		nextOnClock(),
	].filter(Boolean);
	return parts.join(' · ').replace(/&amp;/g, '&');
};

const buttons = (group, labels) => labels.map((label, i) => `<button type="button" data-group="${group}" data-i="${i}">${escapeHtml(label)}</button>`).join(' ');

const render = (panel) => {
	const screens = loopScreens();
	// a screen the rules are passing by still has its button lit
	const lit = byHand(() => new Set(screens.filter((d) => d.status === STATUS.loaded && d.timing.totalScreens > 0)));
	panel.innerHTML = `<h2>Control room</h2>
		<p class="status"></p>
		<h3>Segments</h3><p>${buttons('feature', FEATURES.map(([, label]) => label))}</p>
		<h3>Bumpers</h3><p>${buttons('bumper', BUMPERS.map(([label]) => label))}</p>
		<h3>Interrupts (marked TEST on screen)</h3><p>${INTERRUPTS.map(([kind, label]) => `<button type="button" data-group="interrupt" data-kind="${kind}">${escapeHtml(label)}</button>`).join(' ')}</p>
		<h3>Jump to a screen</h3><p class="screens">${screens.map((d) => `<button type="button" data-group="screen" data-id="${d.navId}"${lit.has(d) ? '' : ' class="idle" title="No data right now"'}>${d.name}</button>`).join(' ')}</p>
		<p class="note">Segments and interrupts wait for whatever is on air to finish. "Jump" works only on screens that have data now, including ones the loop is passing by for its rules.</p>`;
};

const init = () => {
	if (parseQueryString().controls !== '1') return;
	const panel = document.createElement('section');
	panel.id = 'rhl-control';
	document.querySelector('#divTwc')?.after(panel);
	// the screens finish loading after the page does, so draw again once they've had a moment
	render(panel);
	setTimeout(() => render(panel), 8000);
	setInterval(() => {
		const line = panel.querySelector('.status');
		if (line) line.textContent = status();
	}, 500);

	panel.addEventListener('click', (event) => {
		const button = event.target.closest('button');
		if (!button) return;
		const { group } = button.dataset;
		const i = Number(button.dataset.i);
		let ok = true;
		if (group === 'feature') {
			ensurePlaying();
			const [name, , options] = FEATURES[i];
			ok = name === 'show' ? !busy() && runShow() : runFeature(name, options ?? {});
		} else if (group === 'bumper') {
			ok = runBumper(BUMPERS[i][1]);
		} else if (group === 'interrupt') {
			ensurePlaying();
			ok = forceInterrupt(button.dataset.kind);
		} else if (group === 'screen') {
			const display = getDisplay(Number(button.dataset.id));
			// round the loop rules: a screen the clock is passing by still jumps if it has data
			ok = byHand(() => {
				if (!display || display.status !== STATUS.loaded) return false;
				showDisplay(display);
				return true;
			});
		}
		document.querySelector('#divTwc')?.scrollIntoView({ block: 'nearest' });
		if (!ok) {
			button.classList.add('refused');
			setTimeout(() => button.classList.remove('refused'), 1200);
		}
	});
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
